import React, {ReactElement, useCallback, useMemo} from 'react';

import {MoneyIcon, YieldIcon} from '../../../resources/Icons';
import Col from '../../common/Col';
import Row from '../../common/Row';
import {computePercentageChange} from '../../common/util';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {useAppDispatch} from '../../store';
import {Actions} from '../gameSlice';
import {useCurrentEvent, useGameStatus} from '../hooks';
import {formatMoney} from '../mechanics';
import {getEvent} from '../resources/events';
import ArrowButton from './buttons/ArrowButton';
import InfoPill from './buttons/InfoPill';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';

const Events = (): ReactElement | null => {
  const dispatch = useAppDispatch();
  const gameStatus = useGameStatus();
  const {isMobile} = useBreakpoint('mobile');
  const eventLog = gameStatus.history[gameStatus.currentYear].events;
  const events = useMemo(() => eventLog.map(log => getEvent(log.id)), [eventLog]);

  // Find current event (i.e. last non-silent triggered event)
  const {currentLog, currentEvent, currentIndex} = useCurrentEvent();

  // Find first non-triggered (without effect) event (excluding silent events)
  const nextLogIndex = eventLog.findIndex((log, idx) => !log.effect && !events[idx]?.silent);

  const handleBottomButtonClick = useCallback(() => {
    if (nextLogIndex === -1) {
      dispatch(Actions.wrapUpYear());
    } else {
      dispatch(Actions.triggerEventEffect({year: gameStatus.currentYear, index: nextLogIndex}));
    }
  }, [dispatch, gameStatus.currentYear, nextLogIndex]);

  const hasEffects = useMemo(
    () =>
      currentLog?.effect &&
      (currentLog.effect.moneyAfter !== currentLog.effect.moneyBefore ||
        currentLog.effect.yieldAfter !== currentLog.effect.yieldBefore),
    [currentLog?.effect]
  );

  const InfoPills = useMemo(() => {
    if (!currentLog?.effect) return null;
    const eventMoney = currentLog.effect.moneyAfter - currentLog.effect.moneyBefore;
    const eventYield = computePercentageChange(currentLog.effect.yieldBefore, currentLog.effect.yieldAfter);
    return (
      <>
        {!!eventYield && (
          <InfoPill
            icon={<YieldIcon />}
            sublabel={`${eventYield}%`}
            label={`${currentLog.effect.yieldAfter.toFixed(2)} t/ha/yr`}
            danger={eventYield < 0}
            reversed
          />
        )}
        {!!eventMoney && (
          <InfoPill
            icon={<MoneyIcon className="h-[45px] w-[45px]" />}
            sublabel={formatMoney(eventMoney)}
            label={formatMoney(currentLog.effect.moneyAfter)}
            danger={eventMoney < 0}
            reversed
          />
        )}
      </>
    );
  }, [currentLog?.effect]);

  const bottomButton = useMemo(() => {
    const continueButton = (
      <ArrowButton direction="right" onClick={handleBottomButtonClick}>
        Continue
      </ArrowButton>
    );

    if (!hasEffects || isMobile) {
      return <div className="flex justify-center">{continueButton}</div>;
    }

    return (
      <Row className="justify-between px-28 items-center">
        <Row className="gap-x-12">{InfoPills}</Row>
        <div className="h-24">{continueButton}</div>
      </Row>
    );
  }, [InfoPills, handleBottomButtonClick, hasEffects, isMobile]);

  const topButton = useMemo(
    () => (
      <SimplePill className={'pr-xs'}>
        <Row className={'items-center gap-md'}>
          Events
          {<span className="text-base text-primary-800">{`${(currentIndex ?? 0) + 1}/${eventLog.length}`}</span>}
        </Row>
      </SimplePill>
    ),
    [currentIndex, eventLog.length]
  );

  const iconElement = useMemo(
    () => currentEvent?.icon && <currentEvent.icon className="w-[12rem] h-[12rem] mx-auto" />,
    [currentEvent]
  );

  if (!currentEvent) {
    if (nextLogIndex === -1) {
      // Skip Events phase if there are no events for this year
      dispatch(Actions.wrapUpYear());
      return null;
    } else {
      dispatch(Actions.triggerEventEffect({year: gameStatus.currentYear, index: nextLogIndex}));
      return null;
    }
  }

  return (
    <MessageBox top={topButton} bottom={bottomButton}>
      {currentEvent && (
        <Col className={'mt-xs gap-sm text-center font-semibold px-16 mobile:px-4'}>
          {iconElement}
          <h2 className={'text-3xl font-semibold mobile:text-2xl'}>{currentEvent.name}</h2>
          <h3 className={'text-2xl font-semibold mobile:text-lg'}>{currentEvent.description}</h3>
          {isMobile && hasEffects && <Col className="pt-8 gap-y-6 w-fit mx-auto">{InfoPills}</Col>}
        </Col>
      )}
    </MessageBox>
  );
};

export default Events;
